import {Flex} from "../../components/Container/Flex";
import {DeviceAPI} from "../../utils/API/DeviceAPI";
import React from 'react';
import Device from "../../utils/models/Device";
import TextField from "../../components/Controls/TextField";
import {useTranslation} from "react-i18next";
import Dialog from "../../components/Dialog";
import {DialogActions, DialogContent, DialogTitle, Menu, MenuItem} from "@mui/material";
import TextButton from "../../components/Controls/TextButton";
import {shortDateTimeFormat} from "../../utils/style/formats";
import dayjs from "dayjs";
import LocationAutocomplete from "../../components/Controls/LocationAutocomplete";
import {IconButton} from "../../components/IconButton";
import {ActionsIcon, ClearFilterIcon, RefreshIcon} from "../../utils/icons";
import CircularProgress from "@mui/material/CircularProgress";
import {alpha, Colors} from "../../utils/style/colors";
import {useSnackbar} from "notistack";
import DEVICE_URL_ACTION_MAP from "../../utils/deviceUrlActionMap";

const api = new DeviceAPI();

const search = new URLSearchParams(window.location.search);

const DeviceForm = () => {
    const {enqueueSnackbar} = useSnackbar();
    const [device, setDevice] = React.useState(null);
    const [url, setUrl] = React.useState(null);
    const [isLoading, setLoading] = React.useState(false);
    const [actionsAnchorEl, setActionsAnchorEl] = React.useState(null);
    const [t] = useTranslation();

    React.useEffect(() => {
        if(device) return;
        setLoading(true);
        api.getDeviceByUrl().then(({url, device}) => {
            setUrl(url);
            device = new Device(device);
            for (const [key, value] of search.entries()) {
                if(!device.has(key)) continue;
                device = device.set(key, value);
            }
            const extraFields = [...device.get('extraFields')];
            for(let i in extraFields) {
                const value = search.get(extraFields[i].name);
                if(!value) continue;
                extraFields[i] = {...extraFields[i], value};
                device = device.set('extraFields', extraFields);
            }
            setDevice(device);
            if(search.get('saveNow')) {
                api.saveDeviceByUrl(device).then(() => {
                    window.location.href = window.location.origin + window.location.pathname + '#saved';
                });
            } else {
                setLoading(false);
            }
        });
        if(window.location.hash === '#saved') {
            window.location.hash = '';
            enqueueSnackbar(t('saved'), {variant: 'success'});
        }
    }, [device, enqueueSnackbar, t]);
    
    if(!device || !url) return null;
    const canSave = device.name?.length > 3;
    
    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            sx={{
                "& .MuiDialog-container": {
                    alignItems: "flex-start"   // instead of "center"
                }
            }}
        >
            <DialogTitle>
                <Flex fullWidth align="center">
                    <span style={{flex: 1}}>{url.name}</span>
                    <IconButton 
                        onClick={() => window.location.reload()}
                        disabled={isLoading}
                        tooltip={t('refresh')}>
                        <RefreshIcon />
                    </IconButton>
                </Flex>
            </DialogTitle>
            <DialogContent>
                {isLoading && (
                    <Flex style={{
                        position: 'absolute',
                        background: alpha(Colors.white, 0.7), 
                        left: 0,
                        right: 0,
                        top: 0,
                        bottom: 0,
                        zIndex: 1
                    }} align="center" justify="center">
                        <CircularProgress size={16}  />
                    </Flex>
                )}
                <Flex col gap={6}>
                    <TextField
                        label={t('deviceName')}
                        fullWidth
                        value={device.name}
                        disabled={isLoading || !url.deviceName}
                        onChange={v => setDevice(device.set('name', v))}
                    />
                    {!!url.operatingAddress && (
                        <LocationAutocomplete
                            label={t('operatingAddress')}
                            disabled={isLoading}
                            fullWidth
                            value={device.operatingAddress}
                            onChange={v => setDevice(device.set('operatingAddress', v))}
                        />
                    )}
                    {!!url.tempAddress && (
                        <Flex>
                            <LocationAutocomplete
                                label={t('tempAddress')}
                                helperText={device.tempAddressExpiration ? `${t('availableUntil')}: ${device.tempAddressExpirationDate.format(shortDateTimeFormat)}` : undefined}
                                style={{marginBottom: 12}}
                                disabled={isLoading}
                                fullWidth
                                value={device.tempAddress}
                                onChange={v => setDevice(device.set('tempAddress', v))}
                            />
                            {device.get('tempAddress') && (
                                <IconButton
                                    style={{position: 'absolute', right: 0, top: 4}}
                                    onClick={() => setDevice(device.set('tempAddress', ''))}
                                >
                                    <ClearFilterIcon />
                                </IconButton>
                            )}
                        </Flex>
                    )}
                    {!!url.industry && (
                        <Flex>
                        <TextField
                            label={t('industry')}
                            disabled={isLoading}
                            fullWidth
                            value={device.industry}
                            onChange={v => setDevice(device.set('industry', v))}
                        />
                            {device.get('industry') && (
                                <IconButton
                                    style={{position: 'absolute', right: 0, top: 4}}
                                    onClick={() => setDevice(device.set('industry', ''))}
                                >
                                    <ClearFilterIcon />
                                </IconButton>
                            )}
                        </Flex>
                    )}
                    {!!url.extra && device.get('extraFields').map((f, i) => {
                        if(!url.extra[f.name]) return null;
                        return (
                            <Flex key={f.name}>
                                <TextField
                                    label={f.name}
                                    disabled={isLoading}
                                    fullWidth
                                    helperText={!!f.value && !!f.exp && `${t('availableUntil')}: ${dayjs.unix(f.exp).format(shortDateTimeFormat)}`}
                                    value={f.value}
                                    style={!!f.value && !!f.exp ? {marginBottom: 12} : {}}
                                    onChange={value => {
                                        const newFields = [...device.get('extraFields')];
                                        newFields[i] = {...f, value};
                                        setDevice(device.set('extraFields', newFields));
                                    }}
                                />
                                {f.value && (
                                    <IconButton
                                        style={{position: 'absolute', right: 0, top: 4}}
                                        onClick={() => {
                                            const newFields = [...device.get('extraFields')];
                                            newFields[i] = {...f, value: ''};
                                            setDevice(device.set('extraFields', newFields));
                                        }}
                                    >
                                        <ClearFilterIcon />
                                    </IconButton>
                                )}
                            </Flex>
                        )
                    })}
                </Flex>
            </DialogContent>
            <DialogActions>
                {url.actions?.length > 0 && (
                    <>
                        <TextButton
                            disabled={isLoading}
                            startIcon={<ActionsIcon />}
                            onClick={e => setActionsAnchorEl(e.currentTarget)}
                        >
                            {t('actions')}
                        </TextButton>
                        <Menu
                            anchorEl={actionsAnchorEl}
                            open={Boolean(actionsAnchorEl)}
                            onClose={() => setActionsAnchorEl(null)}
                        >
                            {url.actions.map(a => {
                                const actionDef = DEVICE_URL_ACTION_MAP[a];
                                if (!actionDef) return null;
                                return (
                                    <MenuItem
                                        key={a}
                                        onClick={() => {
                                            setActionsAnchorEl(null);
                                            setLoading(true);
                                            actionDef.execute(device).then(() => {
                                                setLoading(false);
                                                enqueueSnackbar(t(actionDef.labelKey), {variant: 'success'});
                                            });
                                        }}
                                    >
                                        {t(actionDef.labelKey)}
                                    </MenuItem>
                                );
                            })}
                        </Menu>
                        <span style={{flex: 1}} />
                    </>
                )}
                <TextButton
                    color="primary"
                    disabled={!canSave || isLoading}
                    onClick={() => {
                        setLoading(true);
                        api.saveDeviceByUrl(device).then(() => {
                            setDevice(null);
                            enqueueSnackbar(t('saved'), {variant: 'success'});
                        });
                    }}
                >
                    {t('save')}
                </TextButton>
            </DialogActions>
        </Dialog>
    );
};

export default DeviceForm;
